/**
 * vraagDbNaarWizard.js — een rij uit `vragen` (snake_case) naar het vraag-object
 * van de toets-wizard (NieuweToetsPage). Gebruikt door BewerkenPage (bestaande
 * toets openen) en door de CE-vragenbank (ce_vraag.toets_vraag toevoegen).
 *
 * De omgekeerde richting staat in NieuweToetsPage.handleSave → testService.
 */

import { isEigenBron, titelZonderNummer } from './bronvraag';

// DB-type 'fill' heet in de wizard 'invul'
const typeFromDb = (t) => (t === 'fill' ? 'invul' : t);

export function vraagDbNaarWizard(v) {
  // opties kan zijn: [{text, correct, imageUrl?}, ...] (nieuw) OF ['tekst1', 'tekst2'] (oud)
  const rawOpties = v.opties || [];
  const isObjectFormat = rawOpties.length > 0 && typeof rawOpties[0] === 'object' && rawOpties[0] !== null;
  const optiesText = isObjectFormat ? rawOpties.map((o) => o.text || '') : rawOpties;
  const optiesImages = isObjectFormat ? rawOpties.map((o) => o.imageUrl || null) : rawOpties.map(() => null);
  const correctIdx = (() => {
    if (v.type !== 'mc') return null;
    const parsed = parseInt(v.correct_antwoord, 10);
    if (!isNaN(parsed)) return parsed;
    if (isObjectFormat) {
      const idx = rawOpties.findIndex((o) => o.correct === true);
      return idx >= 0 ? idx : 0;
    }
    return 0;
  })();
  return {
    id: v.id,
    type: typeFromDb(v.type),
    tekst: v.tekst || '',
    opties: optiesText,
    optiesImages,
    correct: v.type === 'mc' ? correctIdx
      : v.type === 'tf' ? String(v.correct_antwoord).toLowerCase() === 'true'
        : v.correct_antwoord,
    punten: v.punten ?? (v.type === 'info' ? 0 : 1),
    modelAntwoord: (() => {
      try { const p = JSON.parse(v.antwoordmodel); return Array.isArray(p) ? p : (v.antwoordmodel || ''); } catch (e) { return v.antwoordmodel || ''; }
    })(),
    rtti: v.rtti_niveau || null,
    afbeelding: v.afbeelding_url || null,
    videoUrl: v.video_url || null,
    videoType: v.video_type || null,
    audioUrl: v.audio_url || null,
    maxAudioPlays: v.max_audio_plays || null,
    // 28-09-2026: ook deze velden laden, anders gingen ze bij bewerken verloren
    matchPairs: v.match_paren || [],
    orderItems: v.order_items || [],
    categories: v.categorieen || [],
    categoryItems: v.categorie_items || [],
    selectableText: v.selecteerbare_tekst || '',
    correctSelections: v.correcte_selecties || [],
    leestekst: v.leestekst || null,
    // Eigen bron (bronvraag): het nummer ("Bron 2: ") zet de wizard er bij opslaan weer voor.
    leestekstTitel: (isEigenBron(v) ? titelZonderNummer(v.leestekst_titel) : v.leestekst_titel) || null,
    antwoordConfig: v.antwoord_config || null,
    grafiekConfig: v.grafiek_config || null,
    rubric: v.rubric || null,
    // Migratie 086: alleen zetten als de kolom er is, anders blijft het veld weg.
    ...(v.antwoord_ai_voorstel !== undefined ? { antwoordAiVoorstel: !!v.antwoord_ai_voorstel } : {}),
    // CE-vragenbank (migratie 077)
    inhoud: v.inhoud || null,
    bronInhoud: v.bron_inhoud || null,
    bronId: v.bron_id || null,
    herkomst: v.herkomst || null,
    leerdoelen: [],
  };
}

/**
 * CE-vragen (ce_vraag-rijen met toets_vraag) naar wizard-vragen met nieuwe
 * numerieke id's vanaf `startId`. Vragen met hetzelfde bron_id blijven
 * aaneengesloten (volgorde van de invoer binnen een bron blijft staan).
 */
export function ceVragenNaarWizard(ceVragen, startId) {
  const groepen = [];
  const perBron = new Map();
  for (const cv of ceVragen) {
    const bronId = cv.toets_vraag?.bron_id;
    if (bronId && perBron.has(bronId)) perBron.get(bronId).push(cv);
    else {
      const g = [cv];
      groepen.push(g);
      if (bronId) perBron.set(bronId, g);
    }
  }
  let id = startId;
  return groepen.flat().map((cv) => ({ ...vraagDbNaarWizard({ ...cv.toets_vraag, id: undefined }), id: id++ }));
}

/** Volgende vrije numerieke id; uuid-id's (bewerkmodus) tellen niet mee (anders NaN). */
export function volgendeVraagId(vragen) {
  const nums = (vragen || []).map((v) => v.id).filter((x) => typeof x === 'number' && Number.isFinite(x));
  return nums.length ? Math.max(...nums) + 1 : 1;
}

/** sessionStorage-sleutel: wizard-vragen die de Vragenbank-pagina meegeeft aan een nieuwe toets. */
export const START_VRAGEN_KEY = 'tw-wizard-startvragen';
